import React, { useCallback, useState, useRef, useMemo } from 'react';
import { _ } from '@splunk/ui-utils/i18n';
import { createDOMID } from '@splunk/ui-utils/id';
import LayersTriple from '@splunk/react-icons/LayersTriple';
import {
    selectLayoutStructure,
    selectLayoutType,
    useSelector,
    useDispatch,
    actionMenuButtonClicked,
} from '@splunk/dashboard-state';
import { isBlockItem } from '@splunk/dashboard-utils';
import { ActionButton } from '../ActionButton';
import { ActionMenuPopup } from '../ActionMenuPopup';
import type { BaseButtonProps } from '../BaseButton';
import { LayerMenu, type LayerMenuProps } from './LayerMenu';
import { getButtonTitle } from '../utils/getButtonTitle';

const layersIcon = <LayersTriple />;

export const LayerButton = ({
    dashboardApi,
    itemId,
    screenReaderText,
}: BaseButtonProps): JSX.Element | null => {
    const [open, setOpen] = useState(false);
    const anchor = useRef<HTMLButtonElement | null>(null);
    const dispatch = useDispatch();
    const layerMenuId = useMemo(() => createDOMID(), []);

    // The result of this selector isn't actually important because the DashboardAPI is used for layering.
    // What is important about this selector is that value changes will force the component to rerender.
    const layoutStructure = useSelector(selectLayoutStructure);
    const layoutType = useSelector(selectLayoutType);

    const handleRequestClose = useCallback(() => {
        setOpen(false);
        anchor.current?.focus();
    }, []);

    const handleOpen = useCallback(() => {
        setOpen(true);
    }, []);

    const setRef = useCallback((refEl: HTMLButtonElement) => {
        anchor.current = refEl;
    }, []);

    const adjustOrder = useCallback<LayerMenuProps['adjustOrder']>(
        ({ from, to, event }) => {
            dispatch(
                actionMenuButtonClicked({
                    event,
                })
            );
            // It's not possible to trigger this if dashboardApi is not defined, but typescript doesn't know that.
            dashboardApi?.adjustCanvasItemOrder({ from, to });
            handleRequestClose();
        },
        [dashboardApi, handleRequestClose, dispatch]
    );

    const buttonTitle = getButtonTitle({
        base: _('Layer'),
        screenReaderText,
    });

    if (!itemId || !dashboardApi) {
        return null;
    }

    if (layoutType !== 'absolute' || !Array.isArray(layoutStructure)) {
        return null;
    }

    const order = layoutStructure.filter(isBlockItem).map(({ item }) => item);
    const itemIdx = order.indexOf(itemId);

    // do not display this button if no order, there's only one item, or somehow the itemId isn't in the layout structure
    if (order.length <= 1 || itemIdx === -1) {
        return null;
    }

    return (
        <>
            <ActionButton
                data-test="layer-button"
                icon={layersIcon}
                title={buttonTitle}
                elementRef={setRef}
                onClick={handleOpen}
                aria-haspopup="true"
                aria-expanded={open}
                aria-controls={layerMenuId}
            />
            <ActionMenuPopup onRequestClose={handleRequestClose}>
                <LayerMenu
                    layerMenuId={layerMenuId}
                    open={open}
                    anchor={anchor}
                    onRequestClose={handleRequestClose}
                    itemIdx={itemIdx}
                    front={order.length - 1}
                    adjustOrder={adjustOrder}
                />
            </ActionMenuPopup>
        </>
    );
};
